import { FamilySharedEmail } from '../../components/FamilySharedEmail';

// Final Member Database: full-record download (CSV / Excel) and audited record editing. Administrators only;
// the server additionally requires the Final Member Database to be unlocked.
import React, { useState, useEffect } from 'react';
import { Download, Pencil, Loader2, FileSpreadsheet, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../App';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '../../components/ui/dropdown-menu';

const TYPE_OPTIONS = [['standard', 'Standard'], ['lifetime', 'Lifetime'], ['senior_65plus', 'Age 65+ Lifetime'], ['child_under14', 'Child (under 14)']];
const NICE = (s) => (s || '').replace(/_/g, ' ');

async function downloadFile(path, token) {
  const res = await api.fetchAuth(path, token);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err.detail;
    if (res.status === 423 || (detail && detail.code === 'FINAL_DB_LOCKED')) { window.dispatchEvent(new CustomEvent('gs-final-db-locked')); }
    throw new Error(typeof detail === 'string' ? detail : (detail && detail.message) || `Download failed (${res.status})`);
  }
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') || '';
  const name = (cd.match(/filename="([^"]+)"/) || [])[1] || 'gsn-export';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return name;
}

export function MembersDownloadMenu({ token, search }) {
  const [busy, setBusy] = useState('');
  const go = async (dataset, format, withSearch) => {
    const key = `${dataset}-${format}-${withSearch ? 'view' : 'all'}`;
    setBusy(key);
    try {
      const q = new URLSearchParams({ dataset, format });
      if (withSearch && search) q.set('search', search);
      const name = await downloadFile(`/api/admin/members/export?${q.toString()}`, token);
      toast.success(`Downloaded ${name}`);
    } catch (e) { toast.error(e.message); }
    setBusy('');
  };
  const Item = ({ dataset, format, withSearch, label, testid }) => (
    <DropdownMenuItem onSelect={() => go(dataset, format, withSearch)} disabled={!!busy} data-testid={testid} className="text-sm">
      {format === 'xlsx' ? <FileSpreadsheet className="w-4 h-4 mr-2" /> : <FileText className="w-4 h-4 mr-2" />}{label}
    </DropdownMenuItem>
  );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={!!busy} data-testid="members-download-menu">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} Download
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72" data-testid="members-download-content">
        <DropdownMenuLabel>All members - full records</DropdownMenuLabel>
        <Item dataset="members" format="xlsx" label="Excel (.xlsx)" testid="dl-members-xlsx" />
        <Item dataset="members" format="csv" label="CSV" testid="dl-members-csv" />
        {search && (<>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>This search only ("{search}")</DropdownMenuLabel>
          <Item dataset="members" format="xlsx" withSearch label="Excel (.xlsx)" testid="dl-view-xlsx" />
          <Item dataset="members" format="csv" withSearch label="CSV" testid="dl-view-csv" />
        </>)}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Members who have claimed</DropdownMenuLabel>
        <Item dataset="claimed" format="xlsx" label="Excel (.xlsx)" testid="dl-claimed-xlsx" />
        <Item dataset="claimed" format="csv" label="CSV" testid="dl-claimed-csv" />
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Invitation emails sent (with delivery result)</DropdownMenuLabel>
        <Item dataset="invited" format="xlsx" label="Excel (.xlsx)" testid="dl-invited-xlsx" />
        <Item dataset="invited" format="csv" label="CSV" testid="dl-invited-csv" />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function MemberEditButton({ token, membershipNumber, onSaved }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)} title="Edit record" data-testid={`edit-member-${membershipNumber}`}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
      {open && <MemberEditDialog token={token} membershipNumber={membershipNumber} onClose={() => setOpen(false)} onSaved={onSaved} />}
    </>
  );
}

function MemberEditDialog({ token, membershipNumber, onClose, onSaved }) {
  const [rec, setRec] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.getAuth(`/api/admin/members/${encodeURIComponent(membershipNumber)}/record`, token)
      .then((d) => { setRec(d); setForm({ family_shared_email: !!d.family_shared_email, full_name: d.full_name || '', dob: d.dob || '', gender: d.gender || '', email: d.email || '', phone: d.phone || '', address_line_1: d.address_line_1 || '', address_line_2: d.address_line_2 || '', city: d.city || '', county: d.county || '', postcode: d.postcode || '', membership_type: d.membership_type || 'standard', expires_at: d.expires_at || '', status: d.status || 'active', reason: '' }); })
      .catch((e) => { toast.error(e.message); onClose(); });
  }, [token, membershipNumber, onClose]);
  // Keep the admin's Family shared email tick while they type; it is always sent with an email change and the server ties it to the new address.
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const noExpiry = form && (rec?.no_expiry_types || []).includes(form.membership_type);
  const save = async () => {
    setBusy(true);
    try {
      const body = {};
      Object.keys(form).forEach((k) => { if (k === 'reason' || String(form[k]) !== String(rec[k] ?? '')) body[k] = form[k]; });
      if (noExpiry) body.expires_at = '';
      if ('email' in body) body.family_shared_email = !!form.family_shared_email;
      const r = await api.putAuth(`/api/admin/members/${encodeURIComponent(membershipNumber)}/record`, body, token);
      toast.success(r.message);
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  // plain render helper (not a component) so inputs keep focus while typing
  const F = ({ k, label, type = 'text', placeholder }) => (
    <div key={k}><Label className="text-xs">{label}</Label><Input type={type} value={form[k]} placeholder={placeholder} onChange={(e) => set(k, e.target.value)} data-testid={`edit-${k}`} /></div>
  );
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="member-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">Edit member record <Badge variant="outline" className="font-mono">{membershipNumber}</Badge></DialogTitle>
          <DialogDescription>The GS membership number cannot be changed, even if the member moves to another postcode or region. Only admins can edit these details. Every saved change is recorded in the audit trail with your name, the old value and the new value.</DialogDescription>
        </DialogHeader>
        {!form ? <div className="h-40 animate-pulse rounded-lg bg-[hsl(var(--muted))]" /> : (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              {F({ k: 'full_name', label: 'Full name' })}
              {F({ k: 'dob', label: 'Date of birth', type: 'date' })}
              <div><Label className="text-xs">Gender</Label>
                <Select value={form.gender || 'unset'} onValueChange={(v) => set('gender', v === 'unset' ? '' : v)}><SelectTrigger data-testid="edit-gender"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="unset">Not recorded</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="male">Male</SelectItem><SelectItem value="other">Other / prefer not to say</SelectItem></SelectContent></Select></div>
              {F({ k: 'email', label: 'Email', type: 'email' })}
              {F({ k: 'phone', label: 'Phone' })}
              {F({ k: 'postcode', label: 'Postcode' })}
              {F({ k: 'address_line_1', label: 'Address line 1' })}
              {F({ k: 'address_line_2', label: 'Address line 2' })}
              {F({ k: 'city', label: 'City' })}
              {F({ k: 'county', label: 'County' })}
              <div><Label className="text-xs">Membership type</Label>
                <Select value={form.membership_type} onValueChange={(v) => set('membership_type', v)}><SelectTrigger data-testid="edit-membership_type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPE_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">Expiry {noExpiry ? '(none - lifetime type)' : ''}</Label><Input type="date" value={noExpiry ? '' : form.expires_at} disabled={noExpiry} onChange={(e) => set('expires_at', e.target.value)} data-testid="edit-expires_at" /></div>
              <div><Label className="text-xs">Record status</Label>
                <Select value={form.status} onValueChange={(v) => set('status', v)}><SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{(rec.status_options || []).map((v) => <SelectItem key={v} value={v}>{NICE(v)}</SelectItem>)}</SelectContent></Select></div>
              <div className="text-xs text-[hsl(var(--muted-foreground))] self-end pb-2">Live status now: <strong>{rec.live_status}</strong>{rec.claim_status ? <> · claim: {NICE(rec.claim_status)}</> : null}{rec.has_card ? <> · card issued (re-rendered silently if name, type or expiry change)</> : null}</div>
            </div>
            <FamilySharedEmail prefix="admin-edit-family-shared-email" checked={form.family_shared_email} required={rec.family_shared_email_required} onCheckedChange={v => set('family_shared_email', v)} />
            <div><Label className="text-xs">Reason for change (recorded in the audit trail)</Label><Textarea rows={2} value={form.reason} onChange={(e) => set('reason', e.target.value)} placeholder="e.g. Member telephoned to correct spelling of surname" data-testid="edit-reason" /></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy || !form} className="bg-[hsl(var(--gs-navy))] text-white" data-testid="edit-save">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
